// ============================================================
// LibraryRow - one character in the session list: name, summary, tags,
// its count while picked, and the hover preview.
// ============================================================
import type { MouseEvent } from 'react';
import type { Character } from '../../engine/character/types';
import styles from '../../styles/CharacterLibrary.module.css';
import clsx from '../../utils/clsx';
import CharacterPreview from '../CharacterPreview';
import { clampCount } from './libraryModel';
import type { LibraryPanel } from './useLibraryPanel';

interface Props {
  character: Character;
  panel: LibraryPanel;
  /** Click adds, right-click (`remove`) takes out, shift takes a range. */
  onPick: (c: Character, e: MouseEvent, remove?: boolean) => void;
  onEdit: (character: Character) => void;
  /** Offer a count on a picked row - either bulk verb reads it. */
  countable: boolean;
}

/** "1 weapon | 2 talents | W:12", and the tags when there are any. */
function CharacterSummary({ character: c }: { character: Character }) {
  const weapons = c.weapons?.length ?? 0;
  const talents = c.talents?.length ?? 0;
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div className={styles.charName}>{c.name}</div>
      <div className={styles.charMeta}>
        {weapons} weapon{weapons !== 1 ? 's' : ''} | {talents} talent
        {talents !== 1 ? 's' : ''} | W:{c.wounds?.max ?? 0}
      </div>
      {c.tags && c.tags.length > 0 && (
        <div className={styles.charTags}>
          {c.tags.map((t) => (
            <span key={t} className={styles.charTag}>
              {t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * HOW MANY OF THIS ONE. Only for a character that is picked - a number
 * beside somebody you are not loading is noise. It stops the click so
 * setting a count does not also change the selection (v8.26.0). It is
 * offered for either verb: staging reads it now, so a GM who can stage
 * but not load still needs it (v8.34.0).
 */
function RowCount({ character: c, panel }: { character: Character; panel: LibraryPanel }) {
  return (
    <input
      type="number"
      min={1}
      max={99}
      className={styles.rowCount}
      aria-label={`How many ${c.name}`}
      data-testid={`library-count-${c.id}`}
      value={panel.counts[c.id] ?? 1}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        const n = clampCount(e.target.value);
        panel.setCounts((prev) => ({ ...prev, [c.id]: n }));
      }}
    />
  );
}

export default function LibraryRow({ character: c, panel, onPick, onEdit, countable }: Props) {
  const { selectedIds } = panel.selection;
  const { hover } = panel;
  const isPicked = selectedIds.has(c.id);
  return (
    // Mouse-only by the user's choice (2026-09-27): row selection is by mouse.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={clsx(
        styles.charRow,
        isPicked && (selectedIds.size === 1 ? styles.charRowSelected : styles.charRowMultiSelected),
        styles.previewAnchor,
      )}
      onClick={(e) => onPick(c, e)}
      onContextMenu={(e) => {
        // Right-click takes it OUT. The browser menu is not what
        // anybody wants over a list they are picking from.
        e.preventDefault();
        onPick(c, e, true);
      }}
      onDoubleClick={() => onEdit(c)}
      onMouseEnter={(e) => {
        // The row's rect, taken NOW: the preview is positioned off it
        // rather than anchored to it, which is what keeps it out of the
        // panel's scroll extent.
        hover.setHoverRect(e.currentTarget.getBoundingClientRect());
        hover.setHoveredId(c.id);
      }}
      onMouseLeave={() => hover.setHoveredId(null)}
    >
      <CharacterSummary character={c} />
      {countable && isPicked && <RowCount character={c} panel={panel} />}
      {hover.hoveredId === c.id && <CharacterPreview character={c} anchor={hover.hoverRect ?? undefined} />}
    </div>
  );
}
